// Загрузка вложений обращения: черновик, presigned PUT, прогресс.
//
// ПОЧЕМУ XHR, А НЕ FETCH. `fetch` не сообщает прогресс ОТДАЧИ тела — только
// приёма ответа. Вложение здесь — до 100 МБ видео, и без полосы интерфейс на
// минуту выглядит зависшим ровно у того человека, у которого уже что-то не
// работает. `XMLHttpRequest.upload.onprogress` это умеет, поэтому он и остался.
//
// Файл грузится ПРЯМО в хранилище, мимо нашего сервера: гнать стомегабайтное
// видео через Fastify — это память, таймауты и заблокированный воркер.
import {
	MAX_SUPPORT_ATTACHMENTS,
	MAX_SUPPORT_ATTACHMENTS_TOTAL_BYTES,
	SUPPORT_ATTACHMENT_TYPES,
	type SupportAttachmentType,
} from "@iec-id/shared/contracts/support";
import { useCallback, useRef, useState } from "react";
import type { PickedFile } from "../components/file-picker";

export type UploadItem = PickedFile & {
	/** id вложения на сервере — им обращение и связывается с файлом. */
	readonly attachmentId: string | null;
	/** Размер в байтах: по нему считается общий потолок ещё до загрузки. */
	readonly bytes: number;
};

/** Черновик обращения: пара «id + секрет», под которую выдаются presigned PUT. */
export type SupportDraftRef = {
	readonly draftId: string;
	readonly draftToken: string;
};

/**
 * Обращения к серверу подставляет приложение.
 *
 * Хук живёт в ките (его зовут и `sso-web`, и `id-web`), а tRPC-клиент у каждого
 * приложения свой — тянуть его сюда значило бы завязать кит на конкретный
 * роутер сервера.
 */
export type SupportUploadPort = {
	createDraft(): Promise<SupportDraftRef>;
	requestUpload(input: {
		readonly draftId: string;
		readonly draftToken: string;
		readonly fileName: string;
		readonly contentType: SupportAttachmentType;
		readonly sizeBytes: number;
	}): Promise<{ readonly attachmentId: string; readonly uploadUrl: string }>;
};

export type SupportUploadErrors = {
	readonly tooMany: string;
	readonly tooLarge: string;
	readonly wrongType: string;
	readonly failed: string;
};

function isAllowed(type: string): type is SupportAttachmentType {
	return (SUPPORT_ATTACHMENT_TYPES as readonly string[]).includes(type);
}

/**
 * Отказать ли файлу ДО загрузки — и с какой формулировкой.
 *
 * Вынесено чистой функцией не ради красоты: накопление считается по уже
 * выбранным файлам, и ошибка здесь стоит человеку выгруженных впустую
 * мегабайт. Чистую функцию можно проверить тестом без DOM, которого у кита
 * нет.
 */
export function rejectionFor(input: {
	readonly items: readonly { readonly bytes: number }[];
	readonly file: { readonly type: string; readonly size: number };
	readonly errors: SupportUploadErrors;
}): string | null {
	if (!isAllowed(input.file.type)) return input.errors.wrongType;
	if (input.items.length >= MAX_SUPPORT_ATTACHMENTS) return input.errors.tooMany;

	const claimed = input.items.reduce((sum, item) => sum + item.bytes, 0);
	if (claimed + input.file.size > MAX_SUPPORT_ATTACHMENTS_TOTAL_BYTES) {
		return input.errors.tooLarge;
	}

	return null;
}

/** «12,4 МБ» — размер человеку, а не байты. */
function formatSize(bytes: number, locale: string): string {
	const mb = bytes / (1024 * 1024);
	const value = mb >= 1 ? mb : bytes / 1024;
	const unit = mb >= 1 ? "MB" : "KB";
	return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value)} ${unit}`;
}

function putWithProgress(
	url: string,
	file: File,
	onProgress: (percent: number) => void,
): Promise<void> {
	return new Promise((resolve, reject) => {
		const request = new XMLHttpRequest();
		request.open("PUT", url);
		request.setRequestHeader("content-type", file.type);
		request.upload.onprogress = (event) => {
			if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
		};
		request.onload = () =>
			request.status >= 200 && request.status < 300
				? resolve()
				: reject(new Error(`upload: HTTP ${request.status}`));
		request.onerror = () => reject(new Error("upload: network"));
		request.onabort = () => reject(new Error("upload: aborted"));
		request.send(file);
	});
}

/**
 * Черновик создаётся ЛЕНИВО — при первом файле или при отправке.
 *
 * Иначе каждое открытие окна поддержки заводило бы строку в базе и тратило
 * лимит: человек, который просто заглянул в форму и закрыл её, оставлял бы за
 * собой мусор и приближал бы себя к отказу «слишком много обращений».
 */
export function useSupportUpload(input: {
	locale: string;
	errors: SupportUploadErrors;
	port: SupportUploadPort;
}) {
	const { port } = input;
	const draftRef = useRef<SupportDraftRef | null>(null);
	const [items, setItems] = useState<UploadItem[]>([]);

	/**
	 * Зеркало состояния для СИНХРОННОГО чтения внутри цикла по файлам.
	 *
	 * `setItems` асинхронный, поэтому при выборе нескольких файлов разом
	 * `items` внутри одного вызова `select` не меняется между итерациями:
	 * счёт и суммарный размер считались бы по нулевому снимку, и проверки
	 * «не больше пяти» и «не больше 100 МБ» пропускали бы всю пачку. Человек
	 * выгружал бы сотни мегабайт, чтобы получить отказ уже от сервера —
	 * ровно то, что эти проверки и должны предотвращать.
	 */
	const itemsRef = useRef<UploadItem[]>([]);

	const applyItems = useCallback((next: (prev: UploadItem[]) => UploadItem[]) => {
		// Считаем от ref, а не от предыдущего state: ref обновляется синхронно
		// и потому виден следующей итерации цикла сразу, не дожидаясь рендера.
		const value = next(itemsRef.current);
		itemsRef.current = value;
		setItems(value);
	}, []);

	const ensureDraft = useCallback(async () => {
		if (draftRef.current) return draftRef.current;
		const draft = await port.createDraft();
		draftRef.current = draft;
		return draft;
	}, [port]);

	const patch = useCallback(
		(id: string, next: Partial<UploadItem>) => {
			applyItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...next } : item)));
		},
		[applyItems],
	);

	const select = useCallback(
		async (files: File[]) => {
			for (const file of files) {
				const localId = `${file.name}:${file.size}:${Math.random().toString(36).slice(2)}`;

				// Три отказа проверяются ДО загрузки: гонять мегабайты, чтобы
				// потом сказать «не тот формат», — это чужой трафик и чужое время.
				const rejection = rejectionFor({
					// Снимок из ref, а не из state: он актуален уже на следующей
					// итерации этого же цикла (см. комментарий у `itemsRef`).
					items: itemsRef.current,
					file: { type: file.type, size: file.size },
					errors: input.errors,
				});

				if (rejection) {
					applyItems((prev) => [
						...prev,
						{
							id: localId,
							attachmentId: null,
							name: file.name,
							sizeLabel: formatSize(file.size, input.locale),
							error: rejection,
							bytes: file.size,
						},
					]);
					continue;
				}

				applyItems((prev) => [
					...prev,
					{
						id: localId,
						attachmentId: null,
						name: file.name,
						sizeLabel: formatSize(file.size, input.locale),
						progress: 0,
						bytes: file.size,
					},
				]);

				try {
					const draft = await ensureDraft();
					const ticket = await port.requestUpload({
						draftId: draft.draftId,
						draftToken: draft.draftToken,
						fileName: file.name,
						contentType: file.type as SupportAttachmentType,
						sizeBytes: file.size,
					});

					await putWithProgress(ticket.uploadUrl, file, (percent) =>
						patch(localId, { progress: percent }),
					);
					patch(localId, { attachmentId: ticket.attachmentId, progress: 100, done: true });
				} catch {
					// Текст ошибки провайдера человеку не показываем: он на
					// английском, содержит внутренние адреса и ничего ему не
					// объясняет. Файл остаётся в списке с понятной подписью —
					// удалить и попробовать снова можно вручную.
					patch(localId, { error: input.errors.failed, progress: null });
				}
			}
		},
		[applyItems, ensureDraft, input.errors, input.locale, patch, port],
	);

	const remove = useCallback(
		(id: string) => {
			// Объект в хранилище остаётся: у анонима нет права удалять по ключу,
			// а непривязанное вложение всё равно уносит суточная уборка
			// черновиков.
			applyItems((prev) => prev.filter((item) => item.id !== id));
		},
		[applyItems],
	);

	const reset = useCallback(() => {
		draftRef.current = null;
		itemsRef.current = [];
		setItems([]);
	}, []);

	return {
		items,
		select,
		remove,
		reset,
		ensureDraft,
		uploading: items.some((item) => typeof item.progress === "number" && !item.done && !item.error),
	};
}
